import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { PERMISSIONS } from "@miplatform/constants";
import { IBidOptionResponse, IOptionPricingResponse } from "@miplatform/types";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { CollapsibleIcon } from "../../../../../components/CollapsibleIcon/CollapsibleIcon";
import {
  OptionPriceDetails,
  OptionPriceSummary,
} from "../OptionPriceDetails/OptionPriceDetails";

export function OptionCard({
  item,
  optionPricing,
  renderActions = () => null,
}: OptionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);
  const { classes } = useStyles();
  const priceDetails = optionPricing.find(
    (pricing) => pricing.planDetails.planOptionId === item.id
  )?.planDetails;

  return (
    <Box className={classes.root}>
      <Box className={classes.cardHeader}>
        <Box className={classes.titleBox}>
          <Typography>{item.name}</Typography>
        </Box>
        <Box className={classes.actionBox}>
          {priceDetails && canReadPricing && (
            <Box className={classes.optionSummary}>
              <OptionPriceSummary priceDetails={priceDetails} />
            </Box>
          )}
          {renderActions(item)}
          {canReadPricing && (
            <CollapsibleIcon
              isCollapsed={isExpanded}
              onCollapse={() => setIsExpanded((prev) => !prev)}
            />
          )}
        </Box>
      </Box>
      {isExpanded && priceDetails && (
        <Box className={classes.priceDetails}>
          <OptionPriceDetails priceDetails={priceDetails} />
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${theme.palette.grey.accent}`,
      backgroundColor: theme.palette.common.white,
      borderRadius: 2,
      "&:hover": {
        boxShadow: `1px 1px 3px ${theme.palette.grey.accent}`,
      },
    },
    cardHeader: {
      display: "flex",
      minHeight: 56,
      padding: theme.spacing(1.5),
    },
    titleBox: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      flex: 1,
    },
    actionBox: {
      display: "flex",
      alignItems: "center",
    },
    priceDetails: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      padding: theme.spacing(0, 2, 2),
      overflowX: "auto",
    },
    optionSummary: {
      display: "flex",
      justifyContent: "flex-end",
    },
  };
});

interface OptionCardProps {
  item: IBidOptionResponse;
  optionPricing: IOptionPricingResponse[];
  renderActions: (option: IBidOptionResponse) => React.ReactNode;
}
